import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link2, Plus, ExternalLink, Pencil, Trash2, X, Check, GripVertical } from 'lucide-react';
import { projectsApi, ProjectLink } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

// Issue #67: Important Links form accepts any text as a URL instead of validating URL format
function isValidUrl(value: string): boolean {
  try {
    const urlToCheck = value.match(/^https?:\/\//i) ? value : `https://${value}`;
    const url = new URL(urlToCheck);
    // Require a dot in the hostname (e.g., example.com) to match backend @IsUrl() validation
    return url.hostname.includes('.');
  } catch {
    return false;
  }
}

interface ImportantLinksProps {
  projectId: string;
  links: ProjectLink[];
  canEdit: boolean;
}

interface SortableLinkItemProps {
  link: ProjectLink;
  canEdit: boolean;
  editingId: string | null;
  onStartEdit: (link: ProjectLink) => void;
  onDelete: (linkId: string) => void;
}

function SortableLinkItem({ link, canEdit, editingId, onStartEdit, onDelete }: SortableLinkItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id, disabled: !canEdit || editingId !== null });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center justify-between group">
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-secondary hover:text-accent transition-colors flex-1 min-w-0"
        >
          <ExternalLink className="w-4 h-4 text-grey-400 flex-shrink-0" />
          <span className="truncate">{link.name}</span>
        </a>
        {canEdit && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!editingId && (
              <button
                className="p-1 text-grey-300 hover:text-grey-500 cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onStartEdit(link)}
              className="p-1 text-grey-400 hover:text-accent"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(link.id)}
              className="p-1 text-grey-400 hover:text-error"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ImportantLinks({ projectId, links, canEdit }: ImportantLinksProps) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newLink, setNewLink] = useState({ name: '', url: '' });
  const [editLink, setEditLink] = useState({ name: '', url: '' });
  const [newLinkError, setNewLinkError] = useState<string | null>(null);
  const [editLinkError, setEditLinkError] = useState<string | null>(null);
  const [sortedLinks, setSortedLinks] = useState(links);

  useEffect(() => {
    setSortedLinks(links);
  }, [links]);

  const sensors = useSensors(useSensor(PointerSensor));

  const createMutation = useMutation({
    mutationFn: (data: { name: string; url: string }) =>
      projectsApi.createLink(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setIsAdding(false);
      setNewLink({ name: '', url: '' });
      setNewLinkError(null);
    },
    onError: () => {
      setNewLinkError('Please enter a valid URL (e.g., https://example.com)');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ linkId, data }: { linkId: string; data: { name?: string; url?: string } }) =>
      projectsApi.updateLink(linkId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setEditingId(null);
      setEditLinkError(null);
    },
    onError: () => {
      setEditLinkError('Please enter a valid URL (e.g., https://example.com)');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => projectsApi.deleteLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (linkIds: string[]) =>
      projectsApi.reorderLinks(projectId, linkIds),
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const handleAdd = () => {
    if (!newLink.name.trim() || !newLink.url.trim()) return;

    if (!isValidUrl(newLink.url.trim())) {
      setNewLinkError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    setNewLinkError(null);

    let url = newLink.url.trim();
    if (!url.match(/^https?:\/\//i)) {
      url = 'https://' + url;
    }

    createMutation.mutate({ name: newLink.name.trim(), url });
  };

  const handleUpdate = () => {
    if (!editingId || !editLink.name.trim() || !editLink.url.trim()) return;

    if (!isValidUrl(editLink.url.trim())) {
      setEditLinkError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    setEditLinkError(null);

    let url = editLink.url.trim();
    if (!url.match(/^https?:\/\//i)) {
      url = 'https://' + url;
    }

    updateMutation.mutate({
      linkId: editingId,
      data: { name: editLink.name.trim(), url },
    });
  };

  const startEdit = (link: ProjectLink) => {
    setEditingId(link.id);
    setEditLink({ name: link.name, url: link.url });
    setEditLinkError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLink({ name: '', url: '' });
    setEditLinkError(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortedLinks.findIndex((l) => l.id === active.id);
    const newIndex = sortedLinks.findIndex((l) => l.id === over.id);
    const newOrder = arrayMove(sortedLinks, oldIndex, newIndex);
    setSortedLinks(newOrder);
    reorderMutation.mutate(newOrder.map((l) => l.id));
  };

  const renderEditForm = () => (
    <div className="space-y-2 p-2 bg-grey-50 rounded-lg">
      <input
        type="text"
        value={editLink.name}
        onChange={(e) => setEditLink((prev) => ({ ...prev, name: e.target.value }))}
        placeholder="Link name"
        className="w-full px-3 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:border-accent focus:ring-accent/20"
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleUpdate();
          if (e.key === 'Escape') cancelEdit();
        }}
      />
      <input
        type="text"
        value={editLink.url}
        onChange={(e) => {
          setEditLink((prev) => ({ ...prev, url: e.target.value }));
          if (editLinkError) setEditLinkError(null);
        }}
        placeholder="https://..."
        className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/20 ${
          editLinkError
            ? 'border-error focus:border-error'
            : 'border-grey-200 focus:border-accent'
        }`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleUpdate();
          if (e.key === 'Escape') cancelEdit();
        }}
      />
      {editLinkError && (
        <p className="text-xs text-error mt-1">{editLinkError}</p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={cancelEdit}
          className="p-1 text-grey-500 hover:text-grey-700"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          onClick={handleUpdate}
          disabled={updateMutation.isPending}
          className="p-1 text-success hover:text-success/80"
        >
          <Check className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderLinkItem = (link: ProjectLink) => (
    <div className="flex items-center justify-between group">
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-sm text-secondary hover:text-accent transition-colors flex-1 min-w-0"
      >
        <ExternalLink className="w-4 h-4 text-grey-400 flex-shrink-0" />
        <span className="truncate">{link.name}</span>
      </a>
    </div>
  );

  // No links: hide the card. Editors keep one line to add the first link,
  // since this card is the only place links are managed.
  if (sortedLinks.length === 0 && !isAdding) {
    if (!canEdit) return null;
    return (
      <button
        onClick={() => {
          setNewLinkError(null);
          setIsAdding(true);
        }}
        className="flex items-center gap-2 px-1 text-sm text-grey-500 hover:text-accent transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add an important link
      </button>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-semibold text-secondary">Important Links</h2>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setNewLinkError(null);
              setIsAdding(true);
            }}
            className="p-1.5 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-accent transition-colors"
            title="Add link"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Links List */}
      <div className="space-y-2">
        {canEdit ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedLinks.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {sortedLinks.map((link) => (
                <div key={link.id}>
                  {editingId === link.id ? (
                    renderEditForm()
                  ) : (
                    <SortableLinkItem
                      link={link}
                      canEdit={canEdit}
                      editingId={editingId}
                      onStartEdit={startEdit}
                      onDelete={(id) => deleteMutation.mutate(id)}
                    />
                  )}
                </div>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          sortedLinks.map((link) => (
            <div key={link.id}>
              {renderLinkItem(link)}
            </div>
          ))
        )}

        {/* Add new link form */}
        {isAdding && (
          <div className="space-y-2 p-2 bg-grey-50 rounded-lg">
            <input
              type="text"
              value={newLink.name}
              onChange={(e) => setNewLink((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Link name (e.g., Staging URL)"
              className="w-full px-3 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:border-accent focus:ring-accent/20"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') {
                  setIsAdding(false);
                  setNewLink({ name: '', url: '' });
                  setNewLinkError(null);
                }
              }}
            />
            <input
              type="text"
              value={newLink.url}
              onChange={(e) => {
                setNewLink((prev) => ({ ...prev, url: e.target.value }));
                if (newLinkError) setNewLinkError(null);
              }}
              placeholder="https://..."
              className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/20 ${
                newLinkError
                  ? 'border-error focus:border-error'
                  : 'border-grey-200 focus:border-accent'
              }`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') {
                  setIsAdding(false);
                  setNewLink({ name: '', url: '' });
                  setNewLinkError(null);
                }
              }}
            />
            {newLinkError && (
              <p className="text-xs text-error mt-1">{newLinkError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAdding(false);
                  setNewLink({ name: '', url: '' });
                  setNewLinkError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!newLink.name.trim() || !newLink.url.trim() || !!newLinkError || createMutation.isPending}
                isLoading={createMutation.isPending}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add link button */}
      {canEdit && !isAdding && (
        <button
          onClick={() => {
            setNewLinkError(null);
            setIsAdding(true);
          }}
          className="flex items-center gap-2 mt-3 text-sm text-grey-500 hover:text-accent transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add a link
        </button>
      )}
    </Card>
  );
}
